import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { chatbotConversationUserActionNames, chatbotConversationUserActionDummyTexts } from '../constants.js';
import { AssistantEphemeralContextSubType, ChatbotConversationUserActionType, ChatbotConversationUserActionSubType } from '../types.js';
import { isFileBasedContext } from '../utils/context.js';
import { useAssistantStore } from '../stores/assistantStore.js';
import { useChatbot } from '../hooks/useChatbot.js';
import { pick } from '@fabric-app/utils/object/manipulation';
import { useQueryResourceDetail } from '../../resources/queries/useQueryResourceDetail.js';
import { capitalizeFirstLetter } from '@fabric-app/utils/text';
import { getResourceGenericName } from '../../resources/utils/getResourceGenericName.js';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { ChatbotErrorCodes } from '@fabric/woody-client';
import { P } from '@fabric-app/ui/components/Typography';
import { getIsResourceMimeTypeSupportedByAssistant } from '../utils/getIsResourceMimeTypeSupportedByAssistant.js';
import { useActiveContextFileProcessing } from '../hooks/useActiveContextFileProcessing.js';
import { useActiveContextFileNotepadStatePending } from '../hooks/useActiveContextFileNotepadStatePending.js';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { cssVar } from '@fabric-app/ui/theme/variables';

const AssistantQuickActions = () => {
  const { ephemeralContext, persistentContext, expandedResource, error } = useChatbot();
  const { setActionInit } = useAssistantStore((s) => pick(s, [
    "setActionInit"
  ]));
  const { subType, value } = ephemeralContext;
  const isPersistentContext = persistentContext.resourceIds.length > 0;
  const { resource, isLoading: isResourceLoading } = useQueryResourceDetail(value, {
    placeholderData: void 0,
    enabled: !isPersistentContext && subType === AssistantEphemeralContextSubType.File
  });
  const contextName = useMemo(() => {
    if (isPersistentContext) return "Current selection";
    if (resource) return capitalizeFirstLetter(getResourceGenericName(resource));
    return "Item";
  }, [
    resource,
    isPersistentContext
  ]);
  const handleAction = (actionType) => {
    setActionInit({
      type: actionType,
      subType: ChatbotConversationUserActionSubType.SELECTION,
      text: chatbotConversationUserActionDummyTexts[actionType]({
        isSelection: isPersistentContext,
        resourceGenericName: resource ? getResourceGenericName(resource) : void 0
      })
    });
  };
  const isContextFileProcessing = useActiveContextFileProcessing();
  const isContextFileNotepadStatePending = useActiveContextFileNotepadStatePending();
  if (isResourceLoading || !isFileBasedContext(ephemeralContext, persistentContext) || !getIsResourceMimeTypeSupportedByAssistant(expandedResource?.mimeType) || error === ChatbotErrorCodes.USAGE_LIMIT_EXCEEDED || isContextFileProcessing) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Conversation.QuickActions, {
    style: {
      width: "calc(100% + 24px)"
    },
    children: [
      /* @__PURE__ */ jsxs(Conversation.QuickActionsContext, {
        style: {
          padding: "0 12px"
        },
        children: [
          contextName,
          " quick actions:",
          isContextFileNotepadStatePending ? /* @__PURE__ */ jsx(Tooltip, {
            delay: 0,
            label: "Saving note...",
            children: /* @__PURE__ */ jsx(Spinner, {
              size: 16,
              color: cssVar["color-text-tertiary"]
            })
          }) : null
        ]
      }),
      /* @__PURE__ */ jsx(Conversation.QuickActionsList, {
        children: Object.values(ChatbotConversationUserActionType).map((it) => /* @__PURE__ */ jsx(Conversation.QuickAction, {
          disabled: isContextFileNotepadStatePending,
          onClick: (e) => {
            e.preventDefault();
            handleAction(it);
          },
          children: /* @__PURE__ */ jsx(P, {
            size: "xs",
            weight: 500,
            color: "inherit",
            children: chatbotConversationUserActionNames[it]
          })
        }, it))
      })
    ]
  });
};

export { AssistantQuickActions };
//# sourceMappingURL=AssistantQuickActions.js.map
